import { useEffect, useRef, useCallback } from 'react';

import { useMutRef } from './useMutRef';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VoidFn = (...args: any[]) => void;

export function useThrottledCallback<T extends VoidFn>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const fnRef = useMutRef(fn);
  const lastArgsRef = useRef<Parameters<T> | null>(null);
  const timeoutRef = useRef<number>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return useCallback(
    (...args: Parameters<T>) => {
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          if (lastArgsRef.current) {
            fnRef.current(...lastArgsRef.current);
          }
          lastArgsRef.current = null;
          timeoutRef.current = undefined;
        }, delay) as unknown as number;
        fnRef.current(...args);
      } else {
        lastArgsRef.current = args;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay],
  );
}
